import { PopoverController } from '@ionic/angular';
import { Constructor } from '@angular/cdk/table';

import { MatDialog } from '@angular/material/dialog';
import { BaseView } from './BaseView';

/**
 * BaseViewPopoverTrait
 *
 * Viewの基底クラス
 * Popover処理の分割クラス
 */
// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
export function BaseViewPopoverTrait<T extends Constructor<{}>>(Target: T) {
return class extends Target {

	////////////////////////////////////////////////////////////////////////////////////
	// ポップアップ操作

	// IonicのPopoverController(継承先のconstructorでinjectionして上書き)
	public popoverCtrl: PopoverController;

	// AngularのMatDialgのコントローラ
	public dialogCtrl: MatDialog;

	/**
	 * showPopoverWindow
	 *
	 * 共通ポップアップ表示。
	 *
	 * 個別の設定をしたポップアップ表示したい場合は、
	 * このメソッドをコピーして実装か、オーバーライドする。
	 * 個別の設定のやり方は、 下にあるcreatePopoverViewメソッド を参照する事。
	 * インスタンスを保持するプロパティを忘れない事。閉じたりできなくなるから。
	 * @param component 表示する中身
	 * @param inputData ポップアップにわたすデータ
	 */
	// eslint-disable-next-line @typescript-eslint/member-ordering
	public popoverWindowInst: any; // インスタンスを保持する。
	public async showPopoverWindow(component: any, inputData = {} ) {

		// 注意：中身のviewClassの start() が呼ばれないのは、BasePopoverContents を継承してないから。

		// 全View共通のポップアップを表示
		// ログインポップアップの表示
		// 消すときの為に実体を保持しておく
		const componentProps = {inputData};
		this.popoverWindowInst = await this.createPopoverView({ component, componentProps });
		const ret = await this.popoverWindowInst.present(); // 表示する

		return ret;

	}

	/**
	 * showDialogWindow
	 *
	 * 共通ポップアップ表示。MatDialog版
	 *
	 * 個別の設定をしたポップアップ表示したい場合は、
	 * このメソッドをコピーして実装か、オーバーライドする。
	 * 個別の設定のやり方は、 下にあるcreatePopoverViewメソッド を参照する事。
	 * インスタンスを保持するプロパティを忘れない事。閉じたりできなくなるから。
	 * @param component 表示する中身
	 * @param inputData ポップアップにわたすデータ
	 */
	public dialogWindowInst: any; // インスタンスを保持する。
	public async showDialogWindow(component: any, inputData = {} ) {

		// 注意：中身のviewClassの start() が呼ばれないのは、BasePopoverContents を継承してないから。

		// 全View共通のポップアップを表示
		// ログインポップアップの表示
		// 消すときの為に実体を保持しておく
		const data = inputData;
		this.dialogWindowInst = await this.openDialogView(component, { data });

		return this.dialogWindowInst;

	}

	/**
	 * dismissPopoverWindow
	 * ポップアップを閉じるメソッド
	 */
	public async dismissPopoverWindow() {
		// 全View共通のポップアップをクローズ
		if (this.popoverWindowInst) {
			await this.popoverWindowInst.dismiss();
		}
	}

	/**
	 * dismissDialogWindow
	 * ポップアップを閉じるメソッド。MatDialog版
	 */
	public async dismissDialogWindow() {
		// 全View共通のポップアップをクローズ
		if (this.dialogWindowInst) {
			await this.dialogWindowInst.close();
		}
	}

	////////////////////////////////////////////////////////////////////////////////////
	// クリック位置に出てくるポップアップ表示
	// MatTableの表で、行メニューの表示とかで使う

	/**
	 * showRelativePopoverWindow
	 * クリック位置でポップアップ表示。
	 * @param viewClass 中身のcomponent
	 * @param eventEv 	クリックされた場所で発生したevent
	 * @param data		ポップアップに渡すデータ @input で受け取れる
	 * @param cssStyle 	省略可。指定するスタイル。
	 */
	public async showRelativePopoverWindow(viewClass: any, eventEv: any, data: any, cssStyle = 'list-table-popup-menu' ): Promise<any> {

		// 注意：中身のviewClassの start() が呼ばれないのは、BasePopoverContents を継承してないから。

		const props = {
			component: viewClass,
			translucent: false,		// viewClass を透過にしない
			event: eventEv,			// イベントを渡すと、発生したElementの場所に表示される
			keyboardClose: true,	// キーボードで閉じる
			backdropDismiss: true,	// 背景クリックで閉じる
			cssClass: cssStyle,		// ポップアップメニュー用スタイル
			componentProps: { inputData: data },	// ポップアップに渡すデータ
		};
		const popInst = await this.createPopoverView(props);
		await popInst.present(); // 表示する

		return popInst; // インスタンスは呼び出し元で管理する事

	}



	////////////////////////////////////////////////////////////////////////////////////
	// 遷移していくポップアップの処理

	/**
	 * nextPopOverShowView
	 * 次を表示。初回表示もこれつかう。
	 * 開いたポップアップから、受け取ったcomponentProps.nextMethod でクラスを指定すると、
	 * 開いたポップアップが閉じて、指定したクラスでポップアップが表示される。
	 * 上に乗せて表示していく時も、これを使う。その時の閉じる処理は、dismissPopoverWindow を使う。
	 * また、上に乗せた時は、registorManagePopoverInstで登録しておけば、allCloseManagePopoverInst で
	 * 一括で閉じる事ができる。
	 * @param nextViewClass 表示するView
	 * @param data 表示するViewに受け渡したいデータ @input で受け取れる
	 * @param startViewInst 一括でポップアップ管理する時用。起点となるViewのインスタンスを指定する。
	 * @param cssOption スタイルシートの指定。文字列。
	 */
	public async nextPopOverShowView(nextViewClass: any, data: any, startViewInst: BaseView, cssOption = '' ) {

		// 注意：中身のviewClassの start() が呼ばれないのは、BasePopoverContents を継承してないから。

		// 元のポップアップを閉じておく
		if (this.popoverWindowInst) {
			await this.popoverWindowInst.dismiss();
		}

		// ポップアップに渡すパラメータを指定。次に行くメソッドと閉じるメソッドを渡す
		// 渡されたメソッドをつかって、次のポップアップ表示と終了を行う。
		// これは、コンポーネント間をわたるデータ
		const param = {
			mainData: data,
			startViewInstance: startViewInst, // ポップアップに起点Viewをわたす
			nextMethod: this.nextPopOverShowView.bind(this),
			closeMethod: this.dismissPopoverWindow.bind(this)
		};

		// ポップアップの表示用パラーメータを作成
		// これは、IonPopoverのパラメータ
		const popViewParam = {
			component: nextViewClass,
			componentProps: { receiveCallerData: param },
			// event: event, // これ使わない。使うとクリックした位置にポップアップ表示される。
		};

		// スタイルシートのオプションがあれば、パラメータ増やす
		if (cssOption !== '') {
			// eslint-disable-next-line @typescript-eslint/dot-notation
			popViewParam['cssClass'] = cssOption;
		}

		// 次のポップアップを表示する。データ等は前のポップアップが制御する。
		this.popoverWindowInst = await this.createPopoverView(popViewParam);
		await this.popoverWindowInst.present();		// ポップアップを表示

		return this.popoverWindowInst;

	}


	/**
	 * registorManagePopoverInst
	 * 管理するポップアップを登録
	 * @param target 対象のインスタンス
	 */
	// eslint-disable-next-line @typescript-eslint/member-ordering
	private popoverInstList: Array<HTMLIonPopoverElement> = [];
	public registorManagePopoverInst(target: HTMLIonPopoverElement) {
		// 管理するポップアップを登録
		if (!target) { return; }
		if (!this.popoverInstList) { this.popoverInstList = []; }
		this.popoverInstList.push(target);
	}

	/**
	 * allCloseManagePopoverInst
	 * 管理している全部のポップアップを閉じる
	 */
	public allCloseManagePopoverInst() {

		// 管理している全部のポップアップを閉じる
		this.popoverInstList.forEach( (item) => {
			if (item.dismiss) { item.dismiss(); }
		});

		// 管理してるArrayをクリア HACK: クリアが聞いてなかったので調査が必要
		this.popoverInstList = [];
		// this.popoverInstList.slice(0);

	}


	/**
	 * createPopoverView
	 * @param param パラメータオブジェクト
	 *
	 * 最小は下記で、view の所に中身のコンポーネントクラスを指定して渡す
	 * {component: view}
	 *
	 * 引数のparam で使える構文
	 *{
	 *	component: view, 		// ポップアップ画面用コンポーネント
	 *	translucent: true,		// 透過指定
	 *	keyboardClose: false,	// キーボードで閉じる
	 *	backdropDismiss: false,	// 背景クリックで閉じるのを無効
	 *  event: srcInstance,		// HTMLの$event を入れると、クリックした場所に表示される
	 *}
	 * @see https://ionicframework.com/docs/api/popover
	 */
	// eslint-disable-next-line @typescript-eslint/member-ordering
	public POPOVER_PARAM_DEFAULT = {
		translucent: true,
		keyboardClose: false,   // キーボードで閉じる
		backdropDismiss: false　// 背景クリックで閉じるのを無効
	};
	public createPopoverView(param: any): Promise<HTMLIonPopoverElement> {

		// 注意：中身のviewClassの start() が呼ばれないのは、BasePopoverContents を継承してないから。
		if (!param.component) {
			// *中身に表示するViewが指定されてなければ nullで返す
			return null;
		}

		// 渡されたパラメータで上書きして起動
		const copy = Object.assign({}, this.POPOVER_PARAM_DEFAULT); // コピーにしないと、POPOVER_PARAM_DEFAULTが書き換えられちゃう
		const mixParam = Object.assign(copy, param); // デフォルトをparamで上書き。この時に無いやつは、はえてくる。あれば上書きされる。

		// ポップアップ画面のインスタンスを作るPromiseを返す。
		return this.popoverCtrl.create(mixParam);

		// ポップアップの中身で、start() が実行されないので注意
	}



	/**
	 * openDialogView
	 * @param viewClass 表示する中身のコンポーネント
	 * @param param パラメータオブジェクト
	 */
	public DIALOG_PARAM_DEFAULT = {
		hasBackdrop: true, // モーダルかどうか
		disableClose: true, // escキー、後ろのクリックなどで閉じないモード
		// panelClass: 'custom-dialog-container'
	};
	public openDialogView(viewClass: any, param: any) {

		// 注意：viewClassの start() が呼ばれないのは、BasePopoverContents を継承してないから。
		if (!viewClass) {
			// *中身に表示するViewが指定されてなければ nullで返す
			return null;
		}

		// 渡されたパラメータで上書きして起動
		const copy = Object.assign({}, this.DIALOG_PARAM_DEFAULT); // コピーにしないと、POPOVER_PARAM_DEFAULTが書き換えられちゃう
		const mixParam = Object.assign(copy, param); // デフォルトをparamで上書き。この時に無いやつは、はえてくる。あれば上書きされる。

		// ポップアップ画面のインスタンスを作るPromiseを返す。
		return this.dialogCtrl.open(viewClass, mixParam);

	}

};
}
